import { EditorContent, useEditor } from '@tiptap/react';
import { useSelector } from 'react-redux';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { EditorContainerSizeContext, EditorContext } from './Context';
import { useExtensions } from './useExtensions';
import LinkBubbleMenu from '../menus/LinkBubbleMenu/LinkBubbleMenu';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import DaDeviationBubbleMenu from '../../extensions/extension-deviation/components/DaDeviationBubbleMenu';
import DaVideoBubbleMenu from '../../extensions/extension-video/components/DaVideoBubbleMenu';
import { getContentFromStorage, isWriterContent } from '../utils/tiptap';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import { Selection } from '@tiptap/pm/state';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import DaGalleryBubbleMenu from '../../extensions/extension-gallery/components/DaGalleryBubbleMenu';
import { setRefs } from '@wix/da-ds/pkg/utils/refHelpers';
import { autoScroll } from './helpers/autoScroll';
import DaGifBubbleMenu from '../../extensions/extension-gif/components/DaGifBubbleMenu';
import DreamBubbleMenu from '../menus/DreamBubbleMenu';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import DaMuroModal from '../../extensions/extension-muro/components/DaMuroModal';
import DaDeviationThumbBubbleMenu from '../../extensions/extension-deviation-thumb/components/DaDeviationThumbBubbleMenu';
import { useTranslation } from 'react-i18next';
import ddt from '@wix/da-ddt';
import MessageModal from '@wix/da-ds/pkg/modals/dsPresets/MessageModal';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import { JsonSchemaCleaner } from '../utils/json';
import baseStyles from './styles/base.scss';
import baseViewerStyles from '../Viewer/styles/base.scss';
const Editor = ({ className, children, onReady, onChange, onFocus, onBlur, onDestroy, onSelectMedia, onManageMedia, autoFocus, placeholder, placeholderOptions, characterCountOptions, initialContent, initialSelection, scrollContainerRef, selfScrollContainer, extensionConfig, externalRenderers, cookieType, defaultSize, presetId, autoScrollWhenWithin, autoScrollDistance, withinModal, currentUser, containerPadding })=>{
    const { t } = useTranslation('editor');
    const apiRef = useRef(null);
    const editorRef = useRef(null);
    // We'll expose the editor ref via context
    const { setRef, setIsReady, setIsEmpty } = useContext(EditorContext);
    const { setContainerSize } = useContext(EditorContainerSizeContext);
    // Crash recovery
    const [editorCrashed, setEditorCrashed] = useState(false);
    const handleRecoverData = useCallback(()=>{
        setEditorCrashed(false);
        try {
            var _editorRef_current;
            const fixContent = getContentFromStorage(initialContent);
            if (fixContent) {
                var _editorRef_current1;
                const cleaner = new JsonSchemaCleaner();
                const cleaned = cleaner.clean(fixContent);
                (_editorRef_current1 = editorRef.current) === null || _editorRef_current1 === void 0 ? void 0 : _editorRef_current1.commands.setContent(cleaned, true);
                ddt.alert('editor', 'Editor recovery attempted', [
                    'editor',
                    'fatal'
                ], {
                    content: JSON.stringify(cleaned),
                    before: JSON.stringify(fixContent),
                    presetId
                });
            }
            (_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : _editorRef_current.setEditable(true, false);
        } catch (e) {
            var _editorRef_current2, _editorRef_current3;
            (_editorRef_current2 = editorRef.current) === null || _editorRef_current2 === void 0 ? void 0 : _editorRef_current2.setEditable(true, false);
            ddt.alertOnError(e, 'editor', 'Editor recovery failed', [
                'editor',
                'fatal'
            ], {
                content: JSON.stringify(initialContent),
                presetId
            });
            (_editorRef_current3 = editorRef.current) === null || _editorRef_current3 === void 0 ? void 0 : _editorRef_current3.commands.setContent({
                type: 'doc',
                content: [
                    {
                        type: 'paragraph',
                        content: []
                    }
                ]
            }, true);
        }
    }, [
        initialContent,
        presetId
    ]);
    const csrfToken = useSelector((state)=>getConfigKey(state, 'csrfToken'));
    const shouldFakeApiRequests = useSelector((state)=>getConfigKey(state, 'fakeApiRequests'));
    const { MiniComponent } = useContext(MuroDepContext);
    const selfScrollRef = useRef(null);
    const { ref, dimensions } = useMeasureElementCookie(cookieType, {
        cookieDim: 'width',
        defaultSize
    });
    const isMobile = useContext(MobileContext);
    const handleChange = useCallback(({ editor, transaction })=>{
        onChange && onChange(editor, transaction);
        autoScrollWhenWithin && autoScroll(autoScrollWhenWithin, autoScrollDistance !== null && autoScrollDistance !== void 0 ? autoScrollDistance : 50, editor, scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.current);
        setIsEmpty(editor.isEmpty);
    }, [
        autoScrollDistance,
        autoScrollWhenWithin,
        onChange,
        scrollContainerRef,
        setIsEmpty
    ]);
    const exts = useExtensions(extensionConfig, {
        placeholder,
        placeholderOptions,
        characterCountOptions
    });
    const onFocusHandler = useCallback((props)=>{
        onFocus && onFocus(props);
        // Mobile
        // This is a fix for mobile where the keyboard is animated and it takes
        // time to appear, tiptap already fires scrollIntoView on nearly all inserts
        // but because of the animation the screen changes size after the tiptap call.
        // This then adds a delay so the the scroll happens at the right time.
        setTimeout(()=>{
            if (isMobile) {
                props.editor.commands.scrollIntoView();
            }
        }, 600);
    }, [
        isMobile,
        onFocus
    ]);
    let content;
    try {
        content = getContentFromStorage(initialContent);
        if (content && content.type === 'doc') {
            var _content_content;
            // Deal with invalid nodes in the document, document only allows block type nodes
            // no inlines like text/hardBreak
            // If this is needed more often we should make a processor
            content.content = (_content_content = content.content) === null || _content_content === void 0 ? void 0 : _content_content.filter((n)=>n.type !== 'hardBreak' && n.type !== 'text');
        }
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load the editor markup', e);
    }
    // This handle content errors where the editor can't continue
    const onContentErrorHandler = useCallback(({ editor, error })=>{
        const emitUpdate = false;
        editor.setEditable(false, emitUpdate);
        setEditorCrashed(true);
        ddt.alertOnError(error, 'editor', 'Bad content for tiptap', [
            'editor',
            'fatal'
        ], {
            content: JSON.stringify(content),
            presetId
        });
    }, [
        content,
        presetId
    ]);
    const isWriter = isWriterContent(initialContent);
    const editorOpts = useMemo(()=>{
        return omitBy({
            onUpdate: handleChange,
            extensions: exts,
            autofocus: autoFocus,
            onFocus: onFocusHandler,
            onBlur,
            onDestroy,
            content,
            enableContentCheck: isWriter ? false : true,
            onContentError: onContentErrorHandler,
            enableInputRules: isMobile ? undefined : [
                'da-emote'
            ]
        }, isUndefined);
    }, [
        autoFocus,
        content,
        exts,
        handleChange,
        isMobile,
        isWriter,
        onBlur,
        onContentErrorHandler,
        onDestroy,
        onFocusHandler
    ]);
    const editor = useEditor(editorOpts, []);
    editorRef.current = editor;
    useEffect(()=>{
        // Set initial selection
        if (initialSelection && (editor === null || editor === void 0 ? void 0 : editor.state)) {
            const selection = Selection.fromJSON(editor.state.doc, initialSelection);
            editor.commands.focus();
            editor.commands.setTextSelection(selection);
        }
    }, [
        editor,
        initialSelection
    ]);
    useEffect(()=>{
        if (setRef) {
            setRef(editor);
        }
        if (editor) {
            externalRenderers.muroMini = MiniComponent;
            editor.storage['da-currentUser'] = currentUser;
            editor.storage['da-ext-config'] = extensionConfig;
            editor.storage['da-ext-renderers'] = externalRenderers;
            editor.storage['da-callbacks'] = {
                onSelectMedia,
                onManageMedia
            };
            editor.storage['da-presetId'] = presetId;
            editor.storage['da-inside-modal'] = withinModal;
            // These are only used when an extension isn't able to use redux-saga based networking.
            editor.storage['da-csrf-token'] = csrfToken !== null && csrfToken !== void 0 ? csrfToken : 'csrf-token-missing';
            editor.storage['da-fake-api-requests'] = shouldFakeApiRequests;
        }
        // Signal readyness
        if (!apiRef.current && editor) {
            apiRef.current = {
                focusEditor () {
                    var _editorRef_current;
                    (_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : _editorRef_current.commands.focus();
                },
                blurEditor () {
                    var _editorRef_current;
                    (_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : _editorRef_current.commands.blur();
                },
                clearEditor () {
                    var _editorRef_current;
                    (_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : _editorRef_current.commands.clearContent(true);
                },
                getStateForSave () {
                    var _editorRef_current;
                    var _editorRef_current_getJSON;
                    return {
                        version: '1',
                        document: (_editorRef_current_getJSON = (_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : _editorRef_current.getJSON()) !== null && _editorRef_current_getJSON !== void 0 ? _editorRef_current_getJSON : {}
                    };
                },
                isEmpty () {
                    var _editorRef_current;
                    return !!((_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : _editorRef_current.isEmpty);
                },
                getCurrentSelection () {
                    var _editorRef_current;
                    return (_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : _editorRef_current.view.state.selection;
                },
                setSelection (selection) {
                    var _editorRef_current;
                    (_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : _editorRef_current.commands.setTextSelection(selection.$from.pos);
                },
                uploadFile (file) {
                    var _editorRef_current_commands_daUploadFile, _editorRef_current, _editorRef_current_commands;
                    (_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : (_editorRef_current_commands_daUploadFile = (_editorRef_current_commands = _editorRef_current.commands).daUploadFile) === null || _editorRef_current_commands_daUploadFile === void 0 ? void 0 : _editorRef_current_commands_daUploadFile.call(_editorRef_current_commands, file);
                },
                hasMedia () {
                    var _editorRef_current;
                    let nodeCount = 0;
                    (_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : _editorRef_current.state.doc.descendants((node)=>{
                        if (![
                            'text',
                            'paragraph',
                            'da-emote'
                        ].includes(node.type.name)) {
                            nodeCount++;
                        }
                    });
                    return nodeCount > 0;
                },
                charCount () {
                    var _editorRef_current_storage, _editorRef_current, _editorRef_current_storage1, _editorRef_current1;
                    if (!((_editorRef_current = editorRef.current) === null || _editorRef_current === void 0 ? void 0 : (_editorRef_current_storage = _editorRef_current.storage) === null || _editorRef_current_storage === void 0 ? void 0 : _editorRef_current_storage.characterCount)) {
                        ddt.warn('editor', 'You need to enable the chat count extension');
                        return 0;
                    }
                    return (_editorRef_current1 = editorRef.current) === null || _editorRef_current1 === void 0 ? void 0 : (_editorRef_current_storage1 = _editorRef_current1.storage) === null || _editorRef_current_storage1 === void 0 ? void 0 : _editorRef_current_storage1.characterCount.characters();
                }
            };
            setIsReady(true);
            onReady && onReady(apiRef.current);
        }
    }, [
        editor,
        extensionConfig,
        externalRenderers,
        onSelectMedia,
        onReady,
        setRef,
        setIsReady,
        presetId,
        shouldFakeApiRequests,
        csrfToken,
        onManageMedia,
        withinModal,
        currentUser,
        MiniComponent
    ]);
    useEffect(()=>{
        if (containerPadding) {
            setContainerSize && setContainerSize(dimensions.width - containerPadding);
        } else {
            setContainerSize && setContainerSize(dimensions.width);
        }
    }, [
        containerPadding,
        dimensions.width,
        setContainerSize
    ]);
    const setScrollAndMeasureRefs = useCallback((node)=>{
        // This fixes the scroll inside the form from just randomly breaking
        // I tried all sorts, removing all scroll listeners etc and this is only
        // thing that fixes it.
        if (selfScrollContainer) {
            const stopProp = (e)=>e.stopPropagation();
            node === null || node === void 0 ? void 0 : node.addEventListener('scroll', stopProp, {
                passive: true
            });
            node === null || node === void 0 ? void 0 : node.addEventListener('wheel', stopProp, {
                passive: true
            });
        }
        setRefs([
            ref,
            selfScrollRef
        ], node);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        selfScrollContainer
    ]);
    const scrollContainer = selfScrollContainer ? selfScrollRef : scrollContainerRef;
    if (!editor) {
        // TODO is this an error state?
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        ref: setScrollAndMeasureRefs,
        className: cn(baseStyles['base-tiptap'], baseViewerStyles['base-tiptap'], className, 'editor-scrollable')
    }, /*#__PURE__*/ React.createElement(EditorContent, {
        editor: editor,
        className: "editor-wrapper"
    }), extensionConfig.link && /*#__PURE__*/ React.createElement(LinkBubbleMenu, {
        scrollContainerRef: scrollContainer
    }), extensionConfig.deviation && /*#__PURE__*/ React.createElement(DaDeviationBubbleMenu, {
        scrollContainerRef: scrollContainer
    }), extensionConfig.daVideo && /*#__PURE__*/ React.createElement(DaVideoBubbleMenu, {
        scrollContainerRef: scrollContainerRef
    }), extensionConfig.deviationThumb && /*#__PURE__*/ React.createElement(DaDeviationThumbBubbleMenu, {
        scrollContainerRef: scrollContainer
    }), extensionConfig.gallery && /*#__PURE__*/ React.createElement(DaGalleryBubbleMenu, {
        scrollContainerRef: scrollContainer
    }), extensionConfig.gif && /*#__PURE__*/ React.createElement(DaGifBubbleMenu, {
        scrollContainerRef: scrollContainer
    }), extensionConfig.muro && /*#__PURE__*/ React.createElement(DreamBubbleMenu, {
        scrollContainerRef: scrollContainer
    }), children === null || children === void 0 ? void 0 : children(editor, scrollContainer), extensionConfig.muro && /*#__PURE__*/ React.createElement(DaMuroModal, {
        editor: editor
    }), /*#__PURE__*/ React.createElement(MessageModal, {
        isOpen: editorCrashed,
        footerButtons: [
            /*#__PURE__*/ React.createElement(SolidButton, {
                key: "recover",
                size: "large",
                variant: "destructive-red",
                onClick: handleRecoverData
            }, t('editor.error.invalid.content.button'))
        ],
        title: t('editor.error.invalid.content.title'),
        message: t('editor.error.invalid.content.message'),
        variant: "horizontal"
    }));
};
Editor.displayName = 'Editor';
export default Editor;

//# sourceMappingURL=Editor.js.map